class WebAudioAPISound {
	private audioContext: AudioContext;
	private bufferList: { [key: string]: AudioBuffer } = {};
	private playingSounds: AudioBufferSourceNode[] = [];
	private gainNode: GainNode | null = null;
	private fadeTimeout: number | null = null;
	private fadeResolve: (() => void) | null = null;
	private isFadingOut: boolean = false;
	private url: string;
	private loop: boolean;
	private volume: number;
	private isLoaded: boolean = false; // Track if the sound is loaded
	private playAfterLoad: boolean = false; // Option to play after load
	private playRetryInterval: number | null = null; // Interval for retrying play
	private currentSource: AudioBufferSourceNode | null = null; // Track the current playing source
	private playbackPosition: number = 0; // Track the current playback position
	private startedAt: number = 0; // AudioContext time when playback started/resumed
	private isPlaying: boolean = false; // Track if the audio is playing
	private isPaused: boolean = false; // Track if the audio is paused
	private maxVolume: number;

	constructor(
		url: string,
		options?: {
			loop?: boolean;
			maxVolume?: number;
			volume?: number;
			playAfterLoad?: boolean;
		},
	) {
		this.url = url;
		this.loop = options?.loop ?? false;
		this.maxVolume = options?.maxVolume ?? 0.2;
		this.volume = options?.volume !== undefined ? options.volume : this.maxVolume;
		this.playAfterLoad = options?.playAfterLoad ?? false;
		// Initialize the AudioContext
		this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

		// Load the sound
		this.loadSound(this.url);
	}

	private async loadSound(url: string) {
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Failed to fetch audio file: ${response.statusText}`);
			}

			const arrayBuffer = await response.arrayBuffer();
			this.audioContext.decodeAudioData(
				arrayBuffer,
				(buffer) => {
					if (!buffer) {
						console.error('Error decoding file data:', url);
						return;
					}
					this.bufferList[url] = buffer;
					this.isLoaded = true; // Mark as loaded

					// Automatically play if the playAfterLoad option is enabled
					if (this.playAfterLoad) {
						this.play();
					}
				},
				(error) => {
					console.error('Error decoding audio data:', error);
				},
			);
		} catch (error) {
			console.error('Error loading sound:', error);
		}
	}

	public play() {
		// If a fade is in progress, cancel it and restore volume before (re)playing.
		this.cancelFadeOut(true);

		// If already playing, treat play() as an idempotent "ensure audible".
		if (this.isPlaying) {
			this.restoreGainToVolume();
			return;
		}
		if (!this.isLoaded) {
			console.warn('Sound not loaded yet. Retrying...');
			this.retryPlayUntilLoaded();
			return;
		}

		const buffer = this.bufferList[this.url];

		if (buffer) {
			if (this.audioContext.state === 'suspended') {
				this.audioContext.resume();
			}

			// Defensive: ensure no previous source keeps playing if state got out of sync
			if (this.currentSource) {
				try {
					this.currentSource.stop(0);
				} catch {
					// best-effort
				}
				this.playingSounds = this.playingSounds.filter((s) => s !== this.currentSource);
				this.currentSource = null;
			}

			const source = this.audioContext.createBufferSource();
			source.buffer = buffer;
			source.loop = this.loop;

			// Create a gain node for volume control
			if (!this.gainNode) {
				this.gainNode = this.audioContext.createGain();
				this.gainNode.connect(this.audioContext.destination);
			}

			source.connect(this.gainNode);
			this.gainNode.gain.value = this.volume;

			this.startedAt = this.audioContext.currentTime;

			source.onended = () => {
				// Fired on natural end and when we call stop().
				// Important: an older source can end after a newer one has started;
				// never let the old source flip isPlaying=false for the new one.
				this.playingSounds = this.playingSounds.filter((s) => s !== source);

				if (this.currentSource === source) {
					this.currentSource = null;
					this.isPlaying = false;
					if (!this.isPaused) {
						this.playbackPosition = 0;
					}
				}
			};

			// Start playback from the saved position if paused
			source.start(0, this.playbackPosition);

			// Track the current playing source
			this.currentSource = source;

			this.isPlaying = true; // Mark as playing
			this.isPaused = false; // Mark as not paused

			// Track the playing sound
			this.playingSounds.push(source);

			// Clear any retry interval if the audio starts playing
			if (this.playRetryInterval) {
				clearInterval(this.playRetryInterval);
				this.playRetryInterval = null;
			}
		} else {
			console.error('Sound not loaded yet.');
		}
	}

	public pause() {
		// Cancel fades so they don't keep ramping in the background.
		this.cancelFadeOut(true);

		this.isPlaying = false; // Mark as not playing
		this.isPaused = true; // Mark as paused
		if (this.currentSource) {
			// Save playback position relative to the buffer
			this.playbackPosition = this.playbackPosition + (this.audioContext.currentTime - this.startedAt);
			this.audioContext.suspend(); // Suspend the AudioContext
			this.currentSource.stop(0); // Stop the current source
			this.currentSource = null; // Clear the current source
		}
	}

	public stop() {
		// Cancel fades so gain is in a sane state for future play().
		this.cancelFadeOut(true);

		if (this.currentSource) {
			this.currentSource.stop(0);
			this.currentSource = null;
		}
		this.playingSounds.forEach((source) => {
			source.stop(0);
		});
		this.playingSounds = [];
		this.playbackPosition = 0; // Reset playback position
		this.isPlaying = false; // Mark as not playing
		this.isPaused = false; // Mark as not paused

		// Clear any retry interval if the audio is stopped
		if (this.playRetryInterval) {
			clearInterval(this.playRetryInterval);
			this.playRetryInterval = null;
		}
	}

	private retryPlayUntilLoaded() {
		if (this.playRetryInterval) {
			return; // Avoid multiple intervals
		}

		this.playRetryInterval = window.setInterval(() => {
			if (this.isLoaded) {
				if (this.isPaused) return; // Avoid playing if paused
				this.play();
			}
		}, 500); // Retry every 500ms
	}

	public setVolume(volume: number) {
		this.volume = volume * this.maxVolume; // Adjust volume to a range of 0 to 1
		if (this.gainNode && !this.isFadingOut) {
			this.gainNode.gain.value = this.volume;
		}
	}

	public fadeOut(durationMs: number = 500): Promise<void> {
		// Cancel any existing fade and begin a new one.
		this.cancelFadeOut(false);
		this.isFadingOut = true;

		// If we don't have a gain node yet, create one so we can fade.
		if (!this.gainNode) {
			this.gainNode = this.audioContext.createGain();
			this.gainNode.connect(this.audioContext.destination);
			this.gainNode.gain.value = this.volume;
		}

		// If context is suspended, ramps won't progress; just set to 0.
		if (this.audioContext.state === 'suspended') {
			this.gainNode.gain.value = 0;
			return Promise.resolve();
		}

		const gainParam = this.gainNode.gain;
		const now = this.audioContext.currentTime;
		const seconds = Math.max(0, durationMs) / 1000;

		// Start from the current value and linearly ramp down to 0.
		try {
			gainParam.cancelScheduledValues(now);
			gainParam.setValueAtTime(gainParam.value, now);
			gainParam.linearRampToValueAtTime(0, now + seconds);
		} catch {
			// Best-effort fallback.
			gainParam.value = 0;
			this.isFadingOut = false;
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			this.fadeResolve = resolve;
			this.fadeTimeout = window.setTimeout(() => {
				this.fadeTimeout = null;
				this.isFadingOut = false;
				this.fadeResolve = null;
				resolve();
			}, durationMs);
		});
	}

	private cancelFadeOut(restoreVolume: boolean) {
		if (!this.isFadingOut && !this.fadeTimeout && !this.fadeResolve) return;

		this.isFadingOut = false;

		if (this.fadeTimeout) {
			clearTimeout(this.fadeTimeout);
			this.fadeTimeout = null;
		}

		if (this.gainNode && this.audioContext && this.audioContext.state !== 'closed') {
			try {
				const now = this.audioContext.currentTime;
				this.gainNode.gain.cancelScheduledValues(now);
				if (restoreVolume) {
					this.gainNode.gain.setValueAtTime(this.volume, now);
				}
			} catch {
				// best-effort
			}
		}

		if (restoreVolume) {
			this.restoreGainToVolume();
		}

		if (this.fadeResolve) {
			const resolve = this.fadeResolve;
			this.fadeResolve = null;
			resolve();
		}
	}

	private restoreGainToVolume() {
		if (!this.gainNode) return;
		try {
			this.gainNode.gain.value = this.volume;
		} catch {
			// best-effort
		}
	}
	public keepAudioContextAlive() {
		// Create a hidden <audio> element
		const audioElement = new Audio();
		audioElement.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA='; // Silent WAV file
		audioElement.loop = false; // Play once
		audioElement.volume = 0; // Ensure no audible sound

		// Play the audio element to keep the AudioContext alive
		audioElement.play().catch((error) => {
			console.error('Error playing silent audio:', error);
		});

		// Clean up the audio element after playback
		audioElement.onended = () => {
			audioElement.remove();
		};
	}
}

export default WebAudioAPISound;
